import React, { useState, FormEvent, ChangeEvent } from "react";
import "./index.less";
import { toast } from "react-toastify";
import service from "../../services/service";
import OTPForm from "../otp-page/OtpPage";
import FlyoutLayout from "../../shared/FlyoutLayout/FlyoutLayout";

interface LoginFormProps {
  isOpen: boolean;
  onSwitch: () => void;
  closeModal: () => void;
  openForgotPassword: () => void;
  setUser: (user: any) => void;
  setToken: (token: string) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSwitch,
  closeModal,
  openForgotPassword,
  isOpen,
  setUser,
  setToken,
}) => {
  const displayName = "cmp-login";

  const mailLogo = new URL("../../assets/mail.svg", import.meta.url).href;
  const lockLogo = new URL("../../assets/lock.svg", import.meta.url).href;
  const view = new URL("../../assets/view.png", import.meta.url).href;
  const hide = new URL("../../assets/hide.png", import.meta.url).href;

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isFormValid = email.trim() !== "" && password.trim() !== "";

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [otpStage, setOtpStage] = useState<boolean>(false);

  // Login handler - may return tokens directly (bypass OTP) or trigger OTP email
  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) newErrors.email = "Email cannot be empty";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Please enter a valid email address";

    if (!password.trim()) newErrors.password = "Password cannot be empty";

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        const response = await service.login(email, password);
        
        // Check if OTP is bypassed and tokens are returned directly
        if (response.access_token) {
          // OTP bypassed - log the user in directly
          localStorage.setItem("access_token", response.access_token);
          localStorage.setItem("refresh_token", response.refresh_token || "");

          // Create user object with available data
          const userData = {
            email: email,
            name: response.name || "",
            id: response.id || "",
            access_token: response.access_token,
            refresh_token: response.refresh_token || "",
          };

          localStorage.setItem("User", JSON.stringify(userData));

          // Update context
          setUser(userData);
          setToken(response.access_token);

          toast.success("Login successful!");
          closeModal();
        } else {
          // OTP required - show OTP form
          toast.success("OTP sent to your email!");
          setOtpStage(true);
        }
      } catch (error: any) {
        if (error.response) {
          toast.error(error.response.data.message || "Login failed");
        } else {
          toast.error("No response from server. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleSignUpClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onSwitch();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (otpStage) {
    return (
      <OTPForm
        isOpen={true}
        email={email}
        closeModal={closeModal}
        setUser={setUser}
        setToken={setToken}
      />
    );
  }

  return (
    <FlyoutLayout
      displayName={displayName}
      isOpen={isOpen}
      closeModal={closeModal}
      contentPadding="100px 32px"
      contentAlign="center"
      contentJustify="center"
    >
      <header className={`${displayName}__header`}>
        <h2 className={`${displayName}__title`}>
          <span className={`${displayName}__title-bold`}>Login </span>to continue
        </h2>
      </header>

      <form
        className={`${displayName}__form`}
        onSubmit={handleLogin}
        noValidate
      >
        {/* Email field */}
        <div className={`${displayName}__field-group`}>
          <label htmlFor="email" className={`${displayName}__label`}>
            Email
          </label>

          <div
            className={`${displayName}__field ${
              errors.email
                ? `${displayName}__field--shake ${displayName}__field--error`
                : ""
            }`}
          >
            <div className={`${displayName}__input-area`}>
              <img className={`${displayName}__icon`} src={mailLogo} alt="" />
              <input
                id="email"
                className={`${displayName}__input`}
                type="email"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
                placeholder="Email"
                autoComplete="username"
                aria-invalid={errors.email ? "true" : "false"}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
            </div>

            {errors.email && (
              <span
                id="email-error"
                className={`${displayName}__error-txt`}
                role="alert"
              >
                {errors.email}
              </span>
            )}
          </div>
        </div>

        {/* Password field */}
        <div className={`${displayName}__field-group`}>
          <label htmlFor="password" className={`${displayName}__label`}>
            Password
          </label>

          <div
            className={`${displayName}__field ${
              errors.password
                ? `${displayName}__field--shake ${displayName}__field--error`
                : ""
            }`}
          >
            <div className={`${displayName}__input-area`}>
              <img className={`${displayName}__icon`} src={lockLogo} alt="" />
              <input
                id="password"
                className={`${displayName}__input`}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                placeholder="Password"
                autoComplete="current-password"
                aria-invalid={errors.password ? "true" : "false"}
                aria-describedby={
                  errors.password ? "password-error" : undefined
                }
              />
              <button
                type="button"
                className={`${displayName}__password-toggle`}
                onClick={togglePasswordVisibility}
                aria-label="Toggle password visibility"
              >
                <img
                  src={showPassword ? hide : view}
                  alt={showPassword ? "Hide password" : "Show password"}
                  className={`${displayName}__password-icon`}
                />
              </button>
            </div>

            {errors.password && (
              <span
                id="password-error"
                className={`${displayName}__error-txt`}
                role="alert"
              >
                {errors.password}
              </span>
            )}
          </div>
        </div>

        <div className={`${displayName}__options`}>
          <label className={`${displayName}__remember`}>
            <input type="checkbox" />
            <span className={`${displayName}__checkbox-text`}>Remember me</span>
          </label>

          <button
            type="button"
            className={`${displayName}__forgot-link`}
            onClick={() => {
              closeModal();
              openForgotPassword();
            }}
          >
            Forgot password?
          </button>
        </div>

        {/* Submit */}
        <div className={`${displayName}__submit-btn`}>
          <button
            className={`${displayName}__submit ${
              isLoading ? `${displayName}__submit--loading` : ""
            }`}
            type="submit"
            disabled={!isFormValid || isLoading}
            aria-label={isLoading ? "Logging in..." : "Login"}
          >
            {isLoading ? "" : "Login"}
          </button>
        </div>
      </form>

      <p className={`${displayName}__signup-text`}>
        Don't have an account?{" "}
        <button
          type="button"
          className={`${displayName}__link`}
          onClick={handleSignUpClick}
        >
          Create One
        </button>
      </p>
    </FlyoutLayout>
  );
};

export default LoginForm;
